import { apiService } from "../services/api";
import type { AppDispatch } from "../state/store";
import { showAlertDialog } from "../state/utils/confirmationDialogUtils";

export const INSPECTION_ADD_ALREADY_RECORDED = "INSPECTION_ADD_ALREADY_RECORDED";

export const INSPECTION_ADD_GUARD_TITLE = "Inspection already continued";

/** Add modules opened from the inspection list. Edit/update does not use this check. */
const INSPECTION_WORKFLOW_ADD_MODULES = new Set([
  "estimation_add",
  "approval_add",
  "jobcard_add",
  "qc_approval_add",
  "invoice_add",
  "gate_pass_issue",
  "job_approval_add",
]);

export type InspectionAddGuardResult = {
  allowed: boolean;
  message: string;
};

export const isInspectionWorkflowAddContext = (
  _tableName?: string | null,
  moduleEndpoint?: string | null
): boolean => {
  const moduleName = String(moduleEndpoint || "").trim();
  if (moduleName === "invoice_add_from_summary") return false;
  return INSPECTION_WORKFLOW_ADD_MODULES.has(moduleName);
};

export const readInspectionAddParentId = (
  ...sources: Array<Record<string, any> | undefined | null>
): string => {
  for (const source of sources) {
    if (!source) continue;
    for (const key of ["parent_id", "parentid"]) {
      const value = source[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }
  }
  return "";
};

export const isInspectionAddGuardFailure = (response?: {
  code?: string;
  message?: string;
} | null): boolean => {
  if (!response) return false;
  if (response.code === INSPECTION_ADD_ALREADY_RECORDED) return true;
  return String(response.message || "").includes(
    "still in add mode and is out of date"
  );
};

export async function showInspectionAddBlockedAlert(
  dispatch: AppDispatch,
  message: string
): Promise<void> {
  await showAlertDialog(
    dispatch,
    message ||
      "This inspection step was already added. This screen is still in add mode and is out of date. The save was not applied. Go back to Inspection and open the existing record.",
    INSPECTION_ADD_GUARD_TITLE
  );
}

export async function validateInspectionWorkflowAdd(params: {
  module: string;
  parentId: string;
}): Promise<InspectionAddGuardResult> {
  const moduleName = String(params.module || "").trim();
  const parentId = String(params.parentId || "").trim();
  if (!moduleName || !parentId) {
    return { allowed: true, message: "" };
  }
  const response = await apiService.post<{
    success?: boolean;
    allowed?: boolean;
    message?: string;
  }>(
    "/pabws/module/inspection_workflow/validate_add",
    { module: moduleName, parent_id: parentId },
    {
      includeAuthToken: true,
      authHeaderType: "Bearer",
      errorHandling: "throw",
    }
  );
  if (response?.success === false) {
    return {
      allowed: false,
      message:
        response.message ||
        "Unable to verify whether this inspection step was already added. Refresh the inspection list and try again.",
    };
  }
  return {
    allowed: response?.allowed !== false,
    message: String(response?.message || ""),
  };
}
